import React from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { calculateReadingTime } from "@/utils/seo";

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  tags: string[];
  media?: {
    url: string;
    alt: string;
  };
  readTime?: string;
}

interface RelatedArticlesProps {
  currentArticleId: string;
  articles: Article[];
  maxItems?: number;
}

export const RelatedArticles = ({ 
  articles = [], 
  maxItems = 3 
}: RelatedArticlesProps) => {
  const relatedArticles = articles
    ?.filter((article) => article && article.id)
    .slice(0, maxItems);

  if (relatedArticles.length === 0) {
    return null;
  }

  const heading = "Related articles picked for you";
  const description =
    "Keep the momentum going with more guides parents turn to when they need actionable help.";

  const buildHref = (article: Article) => {
    return `/articles/${article.id}/${article.slug}`;
  };

  const getReadingTime = (article: Article) => {
    if (article.readTime) return article.readTime;
    const minutes = calculateReadingTime(article.excerpt || "");
    return `${minutes} min read`;
  };

  return (
    <section className="mt-20">
      <div className="relative overflow-hidden rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-sky-100 shadow-xl">
        <div className="px-6 py-8 sm:px-10 sm:py-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
              Keep learning
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {heading}
            </h2>
          </div>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
            {description}
          </p>
        </div>

        <div className="grid gap-6 px-6 pb-8 sm:px-10 sm:pb-10 md:grid-cols-3">
          {relatedArticles.map((article) => (
            <Link
              key={article.id}
              to={buildHref(article)}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/70 bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              aria-label={`Read ${article.title}`}
            >
              {article.media && (
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={article.media.url}
                    alt={article.media.alt || `Image for ${article.title}`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />
                </div>
              )}

              <div className="flex flex-1 flex-col gap-4 p-6">
                <div className="space-y-1">
                  <Badge variant="secondary" className="bg-sky-100 text-sky-700">
                    {article.category}
                  </Badge>
                  <h3 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-sky-700">
                    {article.title}
                  </h3>
                </div>

                <p className="text-sm text-slate-600 line-clamp-3">
                  {article.excerpt.length > 140
                    ? `${article.excerpt.slice(0, 137)}…`
                    : article.excerpt}
                </p>

                <div className="mt-auto flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{getReadingTime(article)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <time dateTime={new Date(article.date).toISOString()}>
                      {article.date}
                    </time>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 text-sm font-semibold text-sky-700">
                  <span>Read article</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
